import { useCallback, useMemo, useRef, useState } from 'react';
import type { ChatParticipant, ChatSession, ExperienceMode, Message, ProgressNode, SessionEvent } from '@localagent/sdk';
import { ChatInterface, type ChatInterfaceHandle, type ChatMessage } from '@localagent/ui';
import { toast } from '@localagent/ui/components/sonner';
import { useParticipantQueue, useParticipantTranscript } from '../../hooks/useChatParticipants';
import { ActivityFeedList } from './ActivityFeedList';
import { mapStepsAndChildren } from './activity-feed';
import { ChatLoadError } from './ChatLoadError';
import { ExecutionControlBar } from './ExecutionControlBar';
import { SubagentMediaApprovals } from './SubagentMediaApprovals';

export interface ParticipantTabViewProps {
  chat: ChatSession;
  participantId: string;
  label: string;
  node: ProgressNode | null;
  events: readonly SessionEvent[];
  getEventData: (seq: number) => Promise<Record<string, unknown>>;
  disabled: boolean;
  testIdPrefix?: string;
  participants: readonly ChatParticipant[];
  mode?: ExperienceMode;
}

// (sessions ADR-1e992, sessions ADR-34ae2, agents ADR-3a333)
const DELIVERY_LABELS: Record<string, string> = {
  'ctx:background-result': 'Background result',
  'ctx:agent-message': 'Message from an agent',
  'ctx:redirect': 'Redirect',
};

function deliveryKind(m: Message): string | undefined {
  return m.role === 'user' && m.name && m.name in DELIVERY_LABELS ? m.name : undefined;
}

// (context ADR-31fe6)
function isLaneContext(m: Message): boolean {
  return m.role === 'user' && typeof m.name === 'string' && m.name !== '' && !(m.name in DELIVERY_LABELS);
}

function DeliveryRow({ message }: { message: ChatMessage }) {
  const kind = String(message.metadata?.deliveryKind);
  const label = DELIVERY_LABELS[kind];
  if (!label) throw new Error(`participant lane row ${message.id} has an unknown name ${kind}`);
  return (
    <div
      data-testid="participant-delivery-row"
      data-delivery-kind={kind}
      className="mx-auto w-full max-w-2xl rounded border border-border/70 bg-muted px-3 py-2"
    >
      <p className="text-caption font-medium text-muted-foreground">{label}</p>
      <p className="whitespace-pre-wrap text-body text-foreground">{message.content}</p>
    </div>
  );
}

function errorText(prefix: string, err: unknown): string {
  return err instanceof Error ? `${prefix}: ${err.message}` : prefix;
}

export function ParticipantTabView({
  chat,
  participantId,
  label,
  node,
  events,
  getEventData,
  disabled,
  testIdPrefix = '',
  participants,
  mode,
}: ParticipantTabViewProps) {
  const transcript = useParticipantTranscript(chat, participantId);
  const queue = useParticipantQueue(chat, participantId);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [loadingNewer, setLoadingNewer] = useState(false);
  const composerRef = useRef<ChatInterfaceHandle | null>(null);
  const inputTextRef = useRef('');
  const [inputHasText, setInputHasText] = useState(false);

  const messages = useMemo<ChatMessage[]>(
    () =>
      transcript.messages
        .filter((m) => (m.role === 'user' || m.role === 'assistant') && !isLaneContext(m))
        .map((m) => {
          const kind = deliveryKind(m);
          return {
            id: m.id,
            role: kind ? ('system' as const) : (m.role as 'user' | 'assistant'),
            content: m.content,
            timestamp: m.created_at ? new Date(m.created_at).toLocaleTimeString() : '',
            metadata: kind ? { ...m.metadata, deliveryKind: kind } : m.metadata,
          };
        }),
    [transcript.messages],
  );

  const gap = useMemo(() => {
    const released = transcript.gap;
    if (!released) return undefined;
    const shown = new Set(messages.map((m) => m.id));
    const after = [...transcript.messages]
      .reverse()
      .find((m) => m.message_index <= released.afterMessageIndex && shown.has(m.id));
    if (!after) return undefined;
    return {
      afterMessageId: after.id,
      label: 'Newer messages not loaded',
    };
  }, [transcript.gap, transcript.messages, messages]);

  const feed = useMemo(() => (node ? mapStepsAndChildren(node.steps, node.children, `tab-${participantId}-`) : []), [node, participantId]);
  const status = participants.find((p) => p.id === participantId)?.status;
  const running = status === 'running';
  const selfLive = running || status === 'idle';

  const send = useCallback(
    (content: string) => {
      chat.sendAsync(content, undefined, undefined, undefined, { participantId }).catch((err: unknown) => {
        toast.error(errorText('Failed to send message', err));
      });
    },
    [chat, participantId],
  );

  const loadOlder = useCallback(() => {
    setLoadingOlder(true);
    transcript
      .loadOlder()
      .catch((err: unknown) => toast.error(errorText('Failed to load earlier messages', err)))
      .finally(() => setLoadingOlder(false));
  }, [transcript]);

  const loadNewer = useCallback(() => {
    setLoadingNewer(true);
    transcript
      .loadNewer()
      .catch((err: unknown) => toast.error(errorText('Failed to load newer messages', err)))
      .finally(() => setLoadingNewer(false));
  }, [transcript]);

  const stop = useCallback(
    (mode: 'turn' | 'all') => {
      try {
        chat.stop({ mode, participantId });
      } catch (err: unknown) {
        toast.error(errorText('Failed to stop', err));
      }
    },
    [chat, participantId],
  );

  const onInputChange = useCallback((value: string) => {
    inputTextRef.current = value;
    const hasText = value.trim().length > 0;
    setInputHasText((prev) => (prev === hasText ? prev : hasText));
  }, []);

  const redirect = useCallback(() => {
    const text = inputTextRef.current.trim();
    if (!text || !running) return;
    try {
      chat.redirect(text, { participantId });
    } catch (err: unknown) {
      toast.error(errorText('Failed to redirect', err));
      return;
    }
    inputTextRef.current = '';
    setInputHasText(false);
    composerRef.current?.restoreInput('');
  }, [chat, participantId, running]);

  if (transcript.error) return <ChatLoadError error={transcript.error} />;

  return (
    <div
      className="h-full"
      data-testid={`${testIdPrefix}participant-tab-view`}
      data-participant-id={participantId}
      data-participant-status={status}
      data-transcript-loaded={transcript.loaded ? 'true' : 'false'}
    >
      <ChatInterface
        ref={composerRef}
        testIdPrefix={`${testIdPrefix}participant-`}
        messages={messages}
        renderMessage={(message) => (message.role === 'system' ? <DeliveryRow message={message} /> : null)}
        onSendMessage={send}
        onInputChange={onInputChange}
        hasOlder={transcript.hasOlder}
        onLoadOlder={transcript.hasOlder ? loadOlder : undefined}
        loadingOlder={loadingOlder}
        hasNewer={gap !== undefined}
        onLoadNewer={gap !== undefined ? loadNewer : undefined}
        loadingNewer={loadingNewer}
        gap={gap}
        onVisibleMessagesChange={transcript.reportVisibleMessages}
        isLoading={running}
        allowSendWhileLoading
        belowMessagesSlot={
          feed.length > 0 ? (
            <div className="space-y-1" data-testid={`${testIdPrefix}participant-steps`}>
              <ActivityFeedList items={feed} events={events} getEventData={getEventData} focusLast={running} />
            </div>
          ) : null
        }
        scrollSignal={(node?.steps.length ?? 0) + (node?.children.length ?? 0)}
        placeholder={running ? `Type a message to queue for ${label}...` : `Message ${label}...`}
        disabled={disabled}
        aboveInputSlot={
          <>
          <SubagentMediaApprovals chat={chat} participants={participants} participantId={participantId} mode={mode} />
          <ExecutionControlBar
            sessionId={chat.sessionId}
            executionState={running ? 'running' : 'idle'}
            queue={queue}
            onStop={() => stop('turn')}
            onStopAll={() => stop('all')}
            backgroundRunning={selfLive || chat.hasLiveWorkBelow(participantId)}
            scope="participant"
            onRemoveQueueItem={(id) => chat.removeQueueItem(id, { participantId })}
            onClearQueue={() => chat.clearQueue({ participantId })}
            onMoveQueueItem={(id, direction) => chat.moveQueueItem(id, direction, { participantId })}
            onEditQueueItem={(id, content) => chat.editQueueItem(id, content, undefined, { participantId })}
            onRedirect={redirect}
            inputHasText={inputHasText}
          />
          </>
        }
      />
    </div>
  );
}
